import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { api } from '@/lib/api-client'
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  Tooltip,
  Legend 
} from 'recharts'
import { TrendingUp, Users, FileText, Activity, DollarSign } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'

export const Route = createFileRoute('/billing/billing-dashboard')({
  component: BillingComponent,
})

// Colors from your UI Mock
const COLORS = ['#00C2FF', '#00E096', '#8884d8', '#FFBB28', '#FF8042'];

// Helper for high-precision currency display
export const formatPreciseCurrency = (value: number | string) => {
  const num = Number(value);
  // If the value is a fraction of a cent, show 8 decimals
  const decimals = num > 0 && num < 1 ? 8 : 2;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
};

interface BillingStats {
  activeClients: number;
  documentsProcessed: number;
  apiCalls: number;
  totalApiCost: number;
  totalRevenue: number;
}

interface ClientBreakdown {
  id: string;
  name: string;
  email: string;
  plan: 'standard' | 'enterprise';
  documents: number;
  apiCalls: number;
  apiCost: number;
  revenue: number;
}

interface DocDistribution {
  type: string;
  count: number;
}

interface ProviderDistribution {
  provider: string;
  cost: number;
}

interface BillingDashboardResponse {
  stats: BillingStats;
  clients: ClientBreakdown[];
  distribution: DocDistribution[];
  providerDistribution: ProviderDistribution[];
}

function BillingComponent() {
  const navigate = useNavigate();

  const handleViewClientBilling = (clientId: string) => {
    if (!clientId) {
      console.error("clientId is undefined!");
      return;
    }
    navigate({ to: '/billing/$clientId', params: { clientId } });
  };

  const { data, isLoading, error } = useQuery<BillingDashboardResponse>({
    queryKey: ['billing-stats'],
    queryFn: async () => {
      const response = await api.billing.dashBoard();
      const result = (response && typeof response === 'object' && 'data' in response) 
        ? response.data 
        : response;
      return result as BillingDashboardResponse;
    },
    refetchInterval: 60000,
  });

  if (isLoading) return <div className="p-8 text-slate-400">Loading...</div>;
  if (error || !data) return <div className="p-8 text-red-400">Error loading billing data</div>;

  const { stats, clients } = data;

  return (
    <div className="min-h-screen bg-[#0B0E14] text-slate-200 p-8 font-sans">
      {/* Header Row / Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
        <StatCard title="ACTIVE CLIENTS" value={stats.activeClients} icon={<Users size={16}/>} />
        <StatCard title="DOCUMENTS PROCESSED" value={stats.documentsProcessed} icon={<FileText size={16}/>} />
        <StatCard title="API CALLS" value={stats.apiCalls.toLocaleString()} icon={<Activity size={16}/>} />
        <StatCard 
            title="YOUR API COST" 
            value={formatPreciseCurrency(stats.totalApiCost)} 
            valueClass="text-orange-400" 
            icon={<TrendingUp size={16}/>}
        />
        <StatCard 
            title="REVENUE" 
            value={formatPreciseCurrency(stats.totalRevenue)} 
            valueClass="text-green-400" 
            subtext={stats.totalRevenue > 0 
                ? `${(((stats.totalRevenue - stats.totalApiCost) / stats.totalRevenue) * 100).toFixed(1)}% margin`
                : "0% margin"
            }
            icon={<DollarSign size={16}/>}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <ChartContainer title="Cost by Provider">
            <Donut 
              data={data.providerDistribution} 
              dataKey="cost" 
              nameKey="provider" 
              isCurrency
            />
        </ChartContainer>
        
        <ChartContainer title="Documents by Type">
            <Donut 
              data={data.distribution} 
              dataKey="count" 
              nameKey="type" 
            />
        </ChartContainer>
      </div>

      {/* Client Breakdown Table */}
      <div className="bg-[#151921] rounded-xl border border-slate-800 overflow-hidden">
        <div className="p-5 border-b border-slate-800">
          <h3 className="text-sm font-semibold text-slate-400 tracking-wider">CLIENT BREAKDOWN</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-[#1A1F29] text-slate-500 uppercase text-[10px] tracking-widest">
              <tr>
                <th className="px-6 py-4 font-medium">Client</th>
                <th className="px-6 py-4 font-medium">Plan</th>
                <th className="px-6 py-4 font-medium text-right">Documents</th>
                <th className="px-6 py-4 font-medium text-right">API Calls</th>
                <th className="px-6 py-4 font-medium text-right">API Cost</th>
                <th className="px-6 py-4 font-medium text-right">Revenue</th>
                <th className="px-6 py-4 font-medium text-right">Margin</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
            {clients.map((client: ClientBreakdown) => {
                const apiCost = Number(client.apiCost || 0);
                const revenue = Number(client.revenue || 0);
                const margin = revenue - apiCost;

                return (
                  <tr 
                    key={client.id} 
                    onClick={() => handleViewClientBilling(client.id)}
                    className="hover:bg-slate-800/30 transition-colors cursor-pointer group"
                  >
                      <td className="px-6 py-4 font-medium text-slate-100 group-hover:text-gold-400 transition-colors">
                        {client.name}
                      </td>
                      <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-bold border uppercase tracking-tighter ${
                          client.plan === 'enterprise' ? 'border-cyan-500/50 text-cyan-400 bg-cyan-500/10' : 'border-indigo-500/50 text-indigo-400 bg-indigo-500/10'
                      }`}>
                          {client.plan}
                      </span>
                      </td>
                      <td className="px-6 py-4 text-right">{client.documents}</td>
                      <td className="px-6 py-4 text-right text-slate-400">{client.apiCalls}</td>
                      <td className="px-6 py-4 text-right font-mono text-orange-300">
                          {formatPreciseCurrency(apiCost)}
                      </td>
                      <td className="px-6 py-4 text-right font-mono text-green-400">
                          {formatPreciseCurrency(revenue)}
                      </td>
                      <td className="px-6 py-4 text-right font-mono text-green-500">
                          {formatPreciseCurrency(margin)}
                      </td>
                  </tr>
                )
            })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// --- Sub-components ---

function StatCard({ title, value, icon, valueClass = "text-slate-100", subtext }: any) {
  return (
    <div className="bg-[#151921] p-5 rounded-xl border border-slate-800">
      <div className="flex justify-between items-center mb-3">
        <span className="text-[10px] font-bold text-slate-500 tracking-widest uppercase">{title}</span>
        <span className="text-slate-600">{icon}</span>
      </div>
      <div className={`text-2xl font-bold ${valueClass}`}>{value}</div>
      {subtext && <div className="text-[10px] text-slate-500 mt-1">{subtext}</div>}
    </div>
  )
}

function ChartContainer({ title, children }: any) {
  return (
    <div className="bg-[#151921] p-6 rounded-xl border border-slate-800 h-80 flex flex-col">
      <h3 className="text-sm font-semibold text-slate-400 mb-4 uppercase tracking-wider">{title}</h3>
      <div className="flex-grow">{children}</div>
    </div>
  )
}

function Donut({ data, dataKey, nameKey, isCurrency }: any) {
  if (!data || data.length === 0) return <div className="text-center p-10">No Data</div>;

  const total = data.reduce((acc: number, cur: any) => acc + (Number(cur[dataKey]) || 0), 0);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={data}
          innerRadius={60}
          outerRadius={80}
          paddingAngle={5}
          dataKey={dataKey} 
          nameKey={nameKey}
          stroke="none"
        >
          {data.map((_: any, index: number) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip 
          formatter={(value: any) => {
            const numValue = Number(value || 0);
            const percentage = total > 0 ? ((numValue / total) * 100).toFixed(1) : "0";
            const displayValue = isCurrency ? formatPreciseCurrency(numValue) : numValue;
            
            return [`${displayValue} (${percentage}%)`, nameKey === 'provider' ? 'Cost' : 'Quantity'];
          }}
          contentStyle={{ backgroundColor: '#151921', border: '1px solid #334155', borderRadius: '8px' }}
        />
        <Legend verticalAlign="bottom" height={36}/>
      </PieChart>
    </ResponsiveContainer>
  );
}